/**
 * =============================================================================
 * SESSION PROVIDER
 * =============================================================================
 *
 * React context provider for admin session management.
 * Extracts and manages the BigCommerce signed_payload_jwt from URL.
 *
 * ARCHITECTURE REFERENCE: Section 10 - Admin Dashboard
 * PHASE_0B_SPEC.md: Task 2.4.1
 * PHASE 1 TASK: P1-14 - Token Expiration Tracking
 *
 * Features:
 * - JWT token extraction from URL
 * - Store hash extraction from sub claim
 * - Token expiration tracking with warning before expiry
 *
 * =============================================================================
 */

import React, { createContext, useMemo, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';

/**
 * Number of milliseconds before token expiry to show warning.
 * Default: 5 minutes = 300,000 ms
 */
const EXPIRY_WARNING_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Session context value shape.
 */
export interface SessionContextValue {
  /** The BigCommerce signed_payload_jwt token */
  token: string | null;
  /** Store hash extracted from JWT sub claim */
  storeHash: string | null;
  /** Whether a valid session exists */
  isAuthenticated: boolean;
  /** Token expiration timestamp (ms since epoch), or null if no valid token */
  expiresAt: number | null;
  /** Whether the token is expiring within the warning threshold (5 minutes) */
  isExpiringSoon: boolean;
  /** Minutes until token expires, or null if expired/no token */
  minutesUntilExpiry: number | null;
  /** Callback to dismiss the expiration warning */
  dismissExpiryWarning: () => void;
  /**
   * Re-arm a previously dismissed expiration warning (clears the dismissal so
   * `isExpiringSoon` republishes true while still inside the raw expiry window).
   * ADR-025 B-2 / BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001 (ext B2-F1): a dismissal of
   * the CLEAN warning must NOT suppress the later unsaved-edits warning — the
   * shell calls this when settings transition clean→dirty so §3.1.4's
   * "warn when unsaved edits exist" cannot be bypassed by a prior dismissal.
   */
  resetExpiryWarning: () => void;
}

/**
 * Session context - provides authentication state to the app.
 */
export const SessionContext = createContext<SessionContextValue>({
  token: null,
  storeHash: null,
  isAuthenticated: false,
  expiresAt: null,
  isExpiringSoon: false,
  minutesUntilExpiry: null,
  dismissExpiryWarning: () => {},
  resetExpiryWarning: () => {},
});

/**
 * Props for SessionProvider component.
 */
interface SessionProviderProps {
  children: ReactNode;
}

/**
 * Decodes a JWT payload without verification.
 * We only need to extract claims client-side; verification happens server-side.
 *
 * @param token - The JWT token string
 * @returns The decoded payload or null if invalid
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // JWT payload is the second part, base64url encoded
    const payload = parts[1];

    // Convert base64url to base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Pad if necessary
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');

    // Decode
    const decoded = atob(padded);

    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Extracts store hash from JWT sub claim.
 * The sub claim format is "stores/{store_hash}".
 *
 * @param payload - The decoded JWT payload
 * @returns The store hash or null if not found
 */
function extractStoreHash(payload: Record<string, unknown>): string | null {
  const sub = payload.sub;
  if (typeof sub !== 'string') {
    return null;
  }

  const match = sub.match(/^stores\/(.+)$/);
  return match ? match[1] : null;
}

/**
 * Extracts expiration timestamp from JWT exp claim.
 * JWT exp is in seconds since epoch; we convert to milliseconds.
 *
 * @param payload - The decoded JWT payload
 * @returns The expiration timestamp in ms, or null if not found
 */
function extractExpiration(payload: Record<string, unknown>): number | null {
  const exp = payload.exp;
  if (typeof exp !== 'number') {
    return null;
  }

  // JWT exp is in seconds, convert to milliseconds
  return exp * 1000;
}

/**
 * Extracts the JWT token from the current URL query parameters.
 * BigCommerce passes the token as ?token=...
 *
 * @returns The token string or null if not found
 */
function getTokenFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

/**
 * Base session data extracted from JWT (stable across renders).
 */
interface BaseSessionData {
  token: string | null;
  storeHash: string | null;
  isAuthenticated: boolean;
  expiresAt: number | null;
}

/**
 * SessionProvider component.
 *
 * Extracts the signed_payload_jwt from URL on mount and provides
 * session context to the application.
 *
 * The token is stored in memory only (not localStorage) for security.
 *
 * Features:
 * - Token extraction from URL
 * - Store hash extraction from JWT sub claim
 * - Token expiration tracking with warning 5 minutes before expiry
 *
 * @example
 * ```tsx
 * // In main.tsx
 * root.render(
 *   <SessionProvider>
 *     <App />
 *   </SessionProvider>
 * );
 *
 * // In a component
 * function MyComponent() {
 *   const { token, storeHash, isAuthenticated, isExpiringSoon } = useSession();
 *   // ...
 * }
 * ```
 */
export function SessionProvider({ children }: SessionProviderProps): React.ReactElement {
  // Extract token ONCE on initial render only.
  // This is intentional: BigCommerce admin panel always does a full page load
  // when opening the app, so SPA navigation without reload is not a valid use case.
  // Token is stored in memory for the session lifetime (not localStorage for security).
  const baseSessionData = useMemo<BaseSessionData>(() => {
    const token = getTokenFromUrl();

    if (!token) {
      return {
        token: null,
        storeHash: null,
        isAuthenticated: false,
        expiresAt: null,
      };
    }

    const payload = decodeJwtPayload(token);
    if (!payload) {
      return {
        token: null,
        storeHash: null,
        isAuthenticated: false,
        expiresAt: null,
      };
    }

    const storeHash = extractStoreHash(payload);
    if (!storeHash) {
      return {
        token: null,
        storeHash: null,
        isAuthenticated: false,
        expiresAt: null,
      };
    }

    const expiresAt = extractExpiration(payload);

    return {
      token,
      storeHash,
      isAuthenticated: true,
      expiresAt,
    };
  }, []);

  // State for tracking expiration warning
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  // State for current time - updates with interval for reactive countdown
  // Review fix: MULTI-TASK-REVIEW-POST-P1-23
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Callback to dismiss the expiration warning
  const dismissExpiryWarning = useCallback(() => {
    setWarningDismissed(true);
  }, []);

  // Re-arm a dismissed warning (ADR-025 B-2 / ext B2-F1). Clearing the dismissal
  // republishes `isExpiringSoon` (still gated on the raw expiry window), so the
  // shell can re-surface the warning — with the unsaved-edits note — the moment
  // settings become dirty, even if the operator dismissed the clean warning.
  const resetExpiryWarning = useCallback(() => {
    setWarningDismissed(false);
  }, []);

  // Effect to check token expiration periodically
  useEffect(() => {
    const { expiresAt, isAuthenticated } = baseSessionData;

    // No need to track if not authenticated or no expiration time
    if (!isAuthenticated || expiresAt === null) {
      return;
    }

    /**
     * Check if the token is expiring soon (within threshold).
     */
    function checkExpiration(): void {
      const now = Date.now();
      const timeUntilExpiry = expiresAt! - now;

      if (timeUntilExpiry <= EXPIRY_WARNING_THRESHOLD_MS && timeUntilExpiry > 0) {
        setIsExpiringSoon(true);
      } else if (timeUntilExpiry <= 0) {
        // Token has expired
        setIsExpiringSoon(true);
      }
    }

    // Initial check
    checkExpiration();

    // Check every 30 seconds for smoother countdown
    // Also update nowMs for reactive minutesUntilExpiry display
    const intervalId = setInterval(() => {
      setNowMs(Date.now());
      checkExpiration();
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [baseSessionData]);

  // Calculate minutes until expiry for display
  // Uses nowMs state to ensure countdown updates with each interval tick
  const minutesUntilExpiry = useMemo<number | null>(() => {
    const { expiresAt, isAuthenticated } = baseSessionData;

    if (!isAuthenticated || expiresAt === null) {
      return null;
    }

    const timeUntilExpiry = expiresAt - nowMs;

    if (timeUntilExpiry <= 0) {
      return 0;
    }

    return Math.ceil(timeUntilExpiry / 60000);
  }, [baseSessionData, nowMs]);

  // Build the full session context value
  const sessionValue = useMemo<SessionContextValue>(() => ({
    ...baseSessionData,
    isExpiringSoon: isExpiringSoon && !warningDismissed,
    minutesUntilExpiry,
    dismissExpiryWarning,
    resetExpiryWarning,
  }), [baseSessionData, isExpiringSoon, warningDismissed, minutesUntilExpiry, dismissExpiryWarning, resetExpiryWarning]);

  return (
    <SessionContext.Provider value={sessionValue}>
      {children}
    </SessionContext.Provider>
  );
}
